// Pure diff helpers for the chat-overlay revise preview. The LLM emits a
// complete Recipe each turn, so we compare draft against the original to find
// what actually changed and let the renderer highlight it.
//
// Matching strategy:
//   - ingredients: by normalized name (case-insensitive, trimmed)
//   - steps:       by normalized title, or first 30 chars of text as fallback
//   - macros:      by field name
//   - top-level:   by field name
//
// Returns "unchanged" when there's no base recipe (create mode or first turn).

import type {
  Ingredient,
  IngredientGroup,
  Macros,
  PrepAheadItem,
  Recipe,
  Step,
} from "./types";

export type DiffStatus = "added" | "changed" | "unchanged";

export type IngredientDiff = {
  ing: Ingredient;
  status: DiffStatus;
  previous?: Ingredient;
};

export type GroupDiff = {
  label?: string;
  ingredients: IngredientDiff[];
  isNew?: boolean;  // labeled group whose label didn't exist in the base
};

export type StepDiff = {
  step: Step;
  status: DiffStatus;
  previous?: Step;
};

export type FieldDiff<T> = {
  current: T;
  previous?: T;
  changed: boolean;
};

export type MacrosDiff = Partial<Record<keyof Macros, FieldDiff<number>>>;

export type TopLevelDiff = {
  title?: FieldDiff<string>;
  subtitle?: FieldDiff<string>;
  description?: FieldDiff<string>;
  category?: FieldDiff<string>;
  servings?: FieldDiff<number>;
};

function norm(s: string | undefined): string {
  return (s ?? "").trim().toLowerCase();
}

// Stable string for an ingredient's per-unit nutrition, for comparison. Sorted
// so key order in the stored map can't read as a change. Mirrors
// _macros_per_key.
function macrosPerKey(ing: Ingredient): string {
  const per = ing.macrosPer;
  if (!per) return "";
  const macros = (per.macros ?? {}) as Record<string, number | undefined>;
  const body = Object.keys(macros)
    .sort()
    .filter((k) => macros[k] != null)
    .map((k) => `${k}=${macros[k]}`)
    .join(",");
  return `${per.unit ?? ""}|${body}`;
}

function ingredientChanged(a: Ingredient, b: Ingredient): boolean {
  // Also compare `amount` (display string stored by the Python API) if present,
  // since base/unit may stay numerically equal while the display changes.
  const aAmt = (a as Record<string, unknown>).amount as string | undefined;
  const bAmt = (b as Record<string, unknown>).amount as string | undefined;
  return (
    String(a.base ?? "") !== String(b.base ?? "") ||
    (a.unit ?? "") !== (b.unit ?? "") ||
    (a.note ?? "") !== (b.note ?? "") ||
    (a.category ?? "") !== (b.category ?? "") ||
    macrosPerKey(a) !== macrosPerKey(b) ||
    (aAmt !== undefined || bAmt !== undefined
      ? (aAmt ?? "") !== (bAmt ?? "")
      : false)
  );
}

function stepKey(s: Step): string {
  return norm(s.title) || norm(s.text).slice(0, 30);
}

function stepChanged(a: Step, b: Step): boolean {
  return (
    (a.title ?? "") !== (b.title ?? "") ||
    (a.text ?? "") !== (b.text ?? "") ||
    JSON.stringify(a.bullets ?? []) !== JSON.stringify(b.bullets ?? []) ||
    (a.note ?? "") !== (b.note ?? "") ||
    (a.timer ?? null) !== (b.timer ?? null)
  );
}

export function diffIngredientGroups(
  base: IngredientGroup[] | undefined,
  draft: IngredientGroup[] | undefined,
): { groups: GroupDiff[]; removed: Ingredient[] } {
  const draftGroups = draft ?? [];

  // No base → everything is "unchanged" baseline (create mode).
  if (!base || base.length === 0) {
    return {
      groups: draftGroups.map((g) => ({
        label: g.label,
        ingredients: (g.ingredients ?? []).map((ing) => ({
          ing,
          status: "unchanged" as const,
        })),
      })),
      removed: [],
    };
  }

  // Map normalized name → base ingredient; track base group labels
  const baseMap = new Map<string, Ingredient>();
  const baseLabels = new Set<string>();
  for (const g of base) {
    if (g.label) baseLabels.add(norm(g.label));
    for (const ing of g.ingredients ?? []) {
      baseMap.set(norm(ing.name), ing);
    }
  }

  const seen = new Set<string>();
  const groups: GroupDiff[] = draftGroups.map((g) => ({
    label: g.label,
    isNew: g.label ? !baseLabels.has(norm(g.label)) : false,
    ingredients: (g.ingredients ?? []).map((ing) => {
      const key = norm(ing.name);
      const previous = baseMap.get(key);
      if (!previous) return { ing, status: "added" as const };
      seen.add(key);
      return {
        ing,
        status: ingredientChanged(ing, previous) ? "changed" : "unchanged",
        previous,
      };
    }),
  }));

  const removed: Ingredient[] = [];
  for (const g of base) {
    for (const ing of g.ingredients ?? []) {
      if (!seen.has(norm(ing.name))) removed.push(ing);
    }
  }

  return { groups, removed };
}

export function diffSteps(
  base: Step[] | undefined,
  draft: Step[] | undefined,
): { steps: StepDiff[]; removed: Step[] } {
  const draftSteps = draft ?? [];

  if (!base || base.length === 0) {
    return {
      steps: draftSteps.map((step) => ({ step, status: "unchanged" as const })),
      removed: [],
    };
  }

  const baseMap = new Map<string, Step>();
  for (const s of base) {
    const k = stepKey(s);
    if (k) baseMap.set(k, s);
  }

  const seen = new Set<string>();
  const steps: StepDiff[] = draftSteps.map((step) => {
    const k = stepKey(step);
    const previous = k ? baseMap.get(k) : undefined;
    if (!previous) return { step, status: "added" as const };
    seen.add(k);
    return {
      step,
      status: stepChanged(step, previous) ? "changed" : "unchanged",
      previous,
    };
  });

  const removed: Step[] = [];
  for (const s of base) {
    const k = stepKey(s);
    if (k && !seen.has(k)) removed.push(s);
  }

  return { steps, removed };
}

export function diffMacros(
  base: Macros | undefined,
  draft: Macros | undefined,
): MacrosDiff {
  const out: MacrosDiff = {};
  if (!draft) return out;
  const fields: Array<keyof Macros> = ["calories", "protein", "carbs", "fat", "fiber"];
  for (const f of fields) {
    const current = draft[f];
    if (current == null) continue;
    const previous = base?.[f];
    out[f] = {
      current,
      previous,
      changed: previous != null && previous !== current,
    };
  }
  return out;
}

function topField<T>(current: T | undefined, previous: T | undefined): FieldDiff<T> | undefined {
  if (current == null) return undefined;
  return {
    current,
    previous,
    changed: previous != null && previous !== current,
  };
}

export function diffTopLevel(
  base: Recipe | null | undefined,
  draft: Partial<Recipe> | null | undefined,
): TopLevelDiff {
  if (!draft) return {};
  return {
    title: topField(draft.title, base?.title),
    subtitle: topField(draft.subtitle, base?.subtitle),
    description: topField(draft.description, base?.description),
    category: topField(draft.category, base?.category),
    servings: topField(draft.servings, base?.servings),
  };
}

// ── Flat revision diff ────────────────────────────────────────────────
// Produces the [{path, from, to}] array stored on Revision.fields and rendered
// in the "WHAT CHANGED" section of the Revisions page.

function ingQty(ing: Ingredient): string {
  // Prefer the display-string `amount` (stored by the Python API) when available.
  const amt = (ing as Record<string, unknown>).amount as string | undefined;
  return [
    amt || (ing.base != null ? String(ing.base) : null),
    ing.unit,
    ing.note ? `(${ing.note})` : null,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
}

function stepLabel(s: Step): string {
  return s.title || norm(s.text).slice(0, 40) || "step";
}

function truncate(s: string | null | undefined, n = 100): string | null {
  if (s == null) return null;
  return s.length <= n ? s : s.slice(0, n) + "…";
}

// For an edited from→to pair, return both sides trimmed to the region that
// actually differs plus `context` chars on each side, with … marking elision.
// Keeps the change visible even when a long shared prefix would otherwise
// truncate both sides to look identical. Falls back to a plain head-truncate
// when the text is unchanged (e.g. only a step's note/timer/bullets changed).
// Mirrors _diff_window in mcp/revision_diff.py — keep the two in lockstep.
function diffWindow(a: string, b: string, context = 30, cap = 160): [string, string] {
  if (a === b) return [truncate(a, cap) ?? "", truncate(b, cap) ?? ""];
  const n = Math.min(a.length, b.length);
  let i = 0;
  while (i < n && a[i] === b[i]) i++;
  let ja = a.length;
  let jb = b.length;
  while (ja > i && jb > i && a[ja - 1] === b[jb - 1]) {
    ja--;
    jb--;
  }
  const start = Math.max(0, i - context);
  const endA = Math.min(a.length, ja + context);
  const endB = Math.min(b.length, jb + context);
  const clip = (s: string, lo: number, hi: number) =>
    (lo > 0 ? "…" : "") + s.slice(lo, hi) + (hi < s.length ? "…" : "");
  return [clip(a, start, endA), clip(b, start, endB)];
}

// Which attribute of an ingredient changed — from the structured fields, so the
// renderer never parses the display string. Mirrors _ingredient_change_kind.
function ingredientChangeKind(prev: Ingredient, ing: Ingredient): string {
  const amt = (i: Ingredient) => (i as Record<string, unknown>).amount;
  if (
    String(prev.base ?? "") !== String(ing.base ?? "") ||
    String(amt(prev) ?? "") !== String(amt(ing) ?? "") ||
    (prev.unit ?? "") !== (ing.unit ?? "")
  )
    return "ingredient.quantity";
  if ((prev.note ?? "") !== (ing.note ?? "")) return "ingredient.note";
  if ((prev.category ?? "") !== (ing.category ?? "")) return "ingredient.category";
  // Per-unit nutrition is what makes a per-meal amount correction move the
  // macros, so a change to it is worth naming rather than folding into the
  // generic "changed".
  if (macrosPerKey(prev) !== macrosPerKey(ing)) return "ingredient.macros";
  return "ingredient.changed";
}

// Which aspect of a step changed. Length is measured on FULL text (not the
// windowed display value) so expanded/trimmed is accurate. Mirrors
// _step_change_kind.
function stepChangeKind(prev: Step, s: Step): string {
  const pt = prev.text ?? "";
  const ct = s.text ?? "";
  if (pt !== ct) {
    if (ct.length > pt.length + 4) return "step.expanded";
    if (pt.length > ct.length + 4) return "step.trimmed";
    return "step.reworded";
  }
  if (JSON.stringify(prev.bullets ?? []) !== JSON.stringify(s.bullets ?? [])) return "step.bullets";
  if ((prev.timer ?? null) !== (s.timer ?? null)) return "step.timer";
  if ((prev.note ?? "") !== (s.note ?? "")) return "step.note";
  if ((prev.title ?? "") !== (s.title ?? "")) return "step.reworded";
  return "step.changed";
}

// Head-start jobs are matched by normalized title, the way steps are. Mirrors
// _prep_key / _prep_changed / _prep_change_kind in mcp/revision_diff.py.
function prepKey(p: PrepAheadItem): string {
  return norm(p.title) || norm(p.text).slice(0, 30);
}

function prepChanged(a: PrepAheadItem, b: PrepAheadItem): boolean {
  return (
    (a.title ?? "") !== (b.title ?? "") ||
    (a.text ?? "") !== (b.text ?? "") ||
    (a.when ?? "") !== (b.when ?? "") ||
    (a.storage ?? "") !== (b.storage ?? "")
  );
}

// The timing and the storage line are the two things a cook plans around, so
// name them rather than folding them into a generic "changed".
function prepChangeKind(prev: PrepAheadItem, p: PrepAheadItem): string {
  if ((prev.when ?? "") !== (p.when ?? "")) return "prepAhead.when";
  if ((prev.storage ?? "") !== (p.storage ?? "")) return "prepAhead.storage";
  return "prepAhead.reworded";
}

export type RevisionField = { path: string; from: unknown; to: unknown; kind: string };

export function computeRevisionFields(
  base: Partial<Recipe>,
  next: Partial<Recipe>,
): RevisionField[] {
  const fields: RevisionField[] = [];

  // Top-level fields
  const topPairs: Array<[string, string, unknown, unknown]> = [
    ["Title", "title", base.title, next.title],
    ["Subtitle", "subtitle", base.subtitle, next.subtitle],
    ["Description", "description", base.description, next.description],
    ["Category", "category", base.category, next.category],
    ["Servings", "servings", base.servings, next.servings],
  ];
  for (const [path, kind, from, to] of topPairs) {
    if ((from ?? "") !== (to ?? "")) {
      fields.push({ path, from: from ?? null, to: to ?? null, kind });
    }
  }

  // Macros
  const macroKeys: Array<keyof Macros> = ["calories", "protein", "carbs", "fat", "fiber"];
  for (const k of macroKeys) {
    const from = base.macros?.[k];
    const to = next.macros?.[k];
    if (from !== to) {
      fields.push({ path: k[0].toUpperCase() + k.slice(1), from: from ?? null, to: to ?? null, kind: "macro" });
    }
  }

  // Ingredient groups — detect new/removed labeled groups
  const baseGroupLabels = new Set<string>();
  for (const g of base.ingredientGroups ?? []) {
    if (g.label) baseGroupLabels.add(norm(g.label));
  }
  const nextGroupLabels = new Set<string>();
  for (const g of next.ingredientGroups ?? []) {
    if (g.label) nextGroupLabels.add(norm(g.label));
  }
  for (const g of next.ingredientGroups ?? []) {
    if (g.label && !baseGroupLabels.has(norm(g.label))) {
      fields.push({ path: `Group: ${g.label}`, from: null, to: null, kind: "group.added" });
    }
  }
  for (const g of base.ingredientGroups ?? []) {
    if (g.label && !nextGroupLabels.has(norm(g.label))) {
      fields.push({ path: `Group: ${g.label}`, from: g.label, to: null, kind: "group.removed" });
    }
  }

  // Ingredients — flatten groups, match by normalized name
  const baseIngMap = new Map<string, Ingredient>();
  for (const g of base.ingredientGroups ?? []) {
    for (const ing of g.ingredients ?? []) baseIngMap.set(norm(ing.name), ing);
  }
  const nextIngMap = new Map<string, Ingredient>();
  for (const g of next.ingredientGroups ?? []) {
    for (const ing of g.ingredients ?? []) nextIngMap.set(norm(ing.name), ing);
  }

  for (const [key, nextIng] of nextIngMap) {
    const baseIng = baseIngMap.get(key);
    if (!baseIng) {
      fields.push({ path: nextIng.name, from: null, to: ingQty(nextIng) || nextIng.name, kind: "ingredient.added" });
    } else if (ingredientChanged(nextIng, baseIng)) {
      fields.push({ path: nextIng.name, from: ingQty(baseIng) || null, to: ingQty(nextIng) || null, kind: ingredientChangeKind(baseIng, nextIng) });
    }
  }
  for (const [key, baseIng] of baseIngMap) {
    if (!nextIngMap.has(key)) {
      fields.push({ path: baseIng.name, from: ingQty(baseIng) || baseIng.name, to: null, kind: "ingredient.removed" });
    }
  }

  // Prep-ahead — match by normalized title, same shape as steps
  const basePrepMap = new Map<string, PrepAheadItem>();
  for (const p of base.prepAhead ?? []) {
    const k = prepKey(p);
    if (k) basePrepMap.set(k, p);
  }
  const nextPrepMap = new Map<string, PrepAheadItem>();
  for (const p of next.prepAhead ?? []) {
    const k = prepKey(p);
    if (k) nextPrepMap.set(k, p);
  }

  for (const [k, nextPrep] of nextPrepMap) {
    const basePrep = basePrepMap.get(k);
    if (!basePrep) {
      fields.push({
        path: `Ahead: ${nextPrep.title || prepKey(nextPrep)}`,
        from: null,
        to: truncate(nextPrep.text || nextPrep.title),
        kind: "prepAhead.added",
      });
    } else if (prepChanged(nextPrep, basePrep)) {
      const kind = prepChangeKind(basePrep, nextPrep);
      // Show the field that actually moved: the timing line for a timing
      // change, the storage line for a storage change, the prose otherwise.
      let from: string;
      let to: string;
      if (kind === "prepAhead.when") {
        from = basePrep.when || "—";
        to = nextPrep.when || "—";
      } else if (kind === "prepAhead.storage") {
        from = basePrep.storage || "—";
        to = nextPrep.storage || "—";
      } else {
        [from, to] = diffWindow(
          basePrep.text || basePrep.title || "",
          nextPrep.text || nextPrep.title || "",
        );
      }
      fields.push({
        path: `Ahead: ${nextPrep.title || prepKey(nextPrep)}`,
        from,
        to,
        kind,
      });
    }
  }
  for (const [k, basePrep] of basePrepMap) {
    if (!nextPrepMap.has(k)) {
      fields.push({
        path: `Ahead: ${basePrep.title || k}`,
        from: truncate(basePrep.text || basePrep.title),
        to: null,
        kind: "prepAhead.removed",
      });
    }
  }

  // Steps — match by stepKey, detect changes
  const baseStepMap = new Map<string, Step>();
  for (const s of base.steps ?? []) {
    const k = stepKey(s);
    if (k) baseStepMap.set(k, s);
  }
  const nextStepMap = new Map<string, Step>();
  for (const s of next.steps ?? []) {
    const k = stepKey(s);
    if (k) nextStepMap.set(k, s);
  }

  for (const [k, nextStep] of nextStepMap) {
    const baseStep = baseStepMap.get(k);
    if (!baseStep) {
      fields.push({
        path: `Step: ${stepLabel(nextStep)}`,
        from: null,
        to: truncate(nextStep.text || nextStep.title),
        kind: "step.added",
      });
    } else if (stepChanged(nextStep, baseStep)) {
      const [from, to] = diffWindow(
        baseStep.text || baseStep.title || "",
        nextStep.text || nextStep.title || "",
      );
      fields.push({ path: `Step: ${stepLabel(nextStep)}`, from, to, kind: stepChangeKind(baseStep, nextStep) });
    }
  }
  for (const [k, baseStep] of baseStepMap) {
    if (!nextStepMap.has(k)) {
      fields.push({
        path: `Step: ${stepLabel(baseStep)}`,
        from: truncate(baseStep.text || baseStep.title),
        to: null,
        kind: "step.removed",
      });
    }
  }

  // aiProvenance.modifications — the AI appends one entry per revision.
  // This is the most reliable signal that something changed, so always include
  // new modification notes as fields entries (shows in "WHAT CHANGED" even when
  // the structural diff finds nothing else).
  const baseMods: string[] = base.aiProvenance?.modifications ?? [];
  const nextMods: string[] = next.aiProvenance?.modifications ?? [];
  for (const mod of nextMods.slice(baseMods.length)) {
    if (mod) fields.push({ path: "AI summary", from: null, to: mod, kind: "ai" });
  }

  return fields;
}
